import React from 'react';
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Button } from '../ui/button';
import { BookOpen, HelpCircle } from 'lucide-react';
import { HelpDocExport } from '../HelpDocExport';

export const FieldHelpPopover = ({ t, testid }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        className="inline-flex items-center text-muted-foreground hover:text-[#2563eb] transition-colors"
        data-testid={testid}
        aria-label={`Help: ${t.title}`}
        onClick={(e) => e.stopPropagation()}
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-96 max-w-[90vw] text-xs space-y-2 z-[10000]" side="right" align="start" data-testid={`${testid}-popover`}>
      <p className="font-semibold text-sm">{t.title}</p>
      {t.body.map((b, i) => <p key={i} className="text-muted-foreground leading-relaxed">{b}</p>)}
      {t.example && (
        <div className="p-2 rounded bg-muted leading-relaxed"><span className="font-medium">Example: </span>{t.example}</div>
      )}
      {t.pitfall && (
        <div className="p-2 rounded bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 leading-relaxed"><span className="font-medium">Watch out: </span>{t.pitfall}</div>
      )}
    </PopoverContent>
  </Popover>
);

const IO_HELP_TOPICS = {
  io_parameter: {
    title: 'IO Parameter',
    body: ['The raw IO element this mapping reads. Search the protocol catalogue by ID or name, or pick "Custom ID..." for elements not in the catalogue.'],
    example: 'Search 239 → "239 Ignition" for Teltonika devices.',
    pitfall: 'Two mappings on the same IO ID are allowed but show an amber duplicate warning — Event Rules may resolve the value ambiguously.',
  },
  virtual_io_id: {
    title: 'Virtual IO ID',
    body: ['Automatic ID for this computed parameter (custom1, custom2, …). It behaves like a real IO ID everywhere — telemetry, Event Rule sources, exports.'],
  },
  display_name: {
    title: 'Display Name',
    body: ['The label shown wherever this value appears: Telemetry panel, Fleet cards, map popups and Event Rule source lists.'],
    example: '"Battery Voltage" instead of "IO 66".',
  },
  io_type: {
    title: 'IO Type',
    body: ['The category icon shown next to the value. Purely visual — it does not change how the value is processed.'],
  },
  value_type: {
    title: 'Value Type',
    body: ['How the raw number is interpreted and displayed:', '• Value — numeric with calibration, formula, decimals and units.', '• Logic — 1/0 shown as your ON/OFF text with colours.', '• Percentage — numeric shown as %.', '• String — dictionary lookup to display text.', '• Multi-Parameter — combines two IO elements.'],
    pitfall: 'Computed parameters are always Logic (1/0 → YES/NO labels).',
  },
  units: {
    title: 'Units',
    body: ['Appended after the displayed value. Pick a standard unit or choose Custom to type your own.'],
    example: 'V, km/h, °C, RPM, L.',
  },
  formula: {
    title: 'Formula',
    body: ['Math applied to the value, where X is the raw reading (after the Calibration Table, if configured). Supports +, -, *, /, parentheses.'],
    example: 'X / 1000 converts millivolts to volts; (X * 9 / 5) + 32 converts °C to °F.',
    pitfall: 'The Calibration Table runs FIRST — the formula receives the calibrated number as X.',
  },
  decimal_places: {
    title: 'Decimal Places',
    body: ['Rounds the final numeric result to 0–4 decimals for display.'],
  },
  calibration: {
    title: 'Calibration Table',
    body: ['X→Y interpolation applied BEFORE the formula. Add known points (raw X → real Y) and readings between points are interpolated linearly.', 'Use it for non-linear sensors like fuel tank floats.'],
    example: '0→0, 512→25, 1024→60, 2048→100 turns an irregular sensor curve into litres or %.',
  },
  logic_text: {
    title: 'ON / OFF Text',
    body: ['The text shown when the raw value is 1 (ON) or 0 (OFF), each with an optional colour chip.', 'Event Rules can compare against these labels as well as the raw value — comparisons are case-insensitive.'],
    example: '1 → "Ignition ON" (green), 0 → "Ignition OFF" (red).',
  },
  dictionary: {
    title: 'Dictionary',
    body: ['Maps each raw value to display text, with an optional colour per entry. Raw values without an entry are shown as-is.'],
    example: '50501 → "Telstra AU", 50502 → "Optus AU".',
  },
  multi_parameter: {
    title: 'Multi-Parameter',
    body: ['Combines this IO with a Secondary IO element.', '• Lookup mode — matches the pair "primary:secondary" against a dictionary.', '• Concatenate mode — joins both values using a template.'],
    example: 'MCC + MNC IOs combined to resolve the mobile network operator name.',
  },
  computed_toggle: {
    title: 'Computed Logic Parameter',
    body: ['Creates a virtual IO calculated from other raw IO values with an AND/OR formula. It behaves like a real IO everywhere: telemetry, Fleet cards, Event Rule sources and condition timers.', 'Formula sources reference the protocol\'s raw IO values — they do not need their own mapping first.'],
    pitfall: 'A source IO the device has never reported counts as 0 / false. The "Now:" hints show the live values the formula sees.',
  },
  visibility: {
    title: 'Visibility',
    body: ['Where this mapped value is shown: Live Tracking Telemetry panel, Fleet cards, and whether the IO Type icon is displayed.', '"Hide if Ignition OFF" hides the value while ignition is off — for readings that are meaningless with the engine off.'],
    pitfall: 'Hiding a value does not stop it being recorded or used by Event Rules — it only affects display.',
  },
};

export const IOHelpTip = ({ topic }) => {
  const t = IO_HELP_TOPICS[topic];
  if (!t) return null;
  return <FieldHelpPopover t={t} testid={`io-help-tip-${topic}`} />;
};

const Section = ({ title, children }) => (
  <div className="space-y-2">
    <h3 className="text-sm font-semibold text-[#2563eb]">{title}</h3>
    <div className="text-xs text-slate-700 dark:text-slate-300 space-y-2 leading-relaxed">{children}</div>
  </div>
);

const Pipeline = ({ steps }) => (
  <div className="flex items-stretch gap-0 pt-1">
    {steps.map((st, i) => (
      <React.Fragment key={i}>
        <div className="flex flex-col items-center flex-1 min-w-0">
          <span className="w-2 h-2 rounded-full bg-[#2563eb] my-0.5" />
          <span className="text-[9px] text-slate-600 dark:text-slate-300 text-center leading-tight">{st}</span>
        </div>
        {i < steps.length - 1 && <div className="h-px bg-[#2563eb]/40 flex-1 mt-[7px] min-w-[8px]" />}
      </React.Fragment>
    ))}
  </div>
);

const VALUE_TYPES = [
  ['Value', 'Numeric reading. Optional Calibration Table, then a math Formula (X = raw value), then decimal places and units.', 'Battery mV → Volts: formula X / 1000, 2 decimals, unit V.'],
  ['Logic', 'Binary 1/0 shown as your own ON/OFF text, each with an optional colour.', '1 → "Ignition ON" (green), 0 → "Ignition OFF" (red).'],
  ['Percentage', 'Numeric reading displayed with a % unit and progress styling.', 'Fuel level IO shown as 0–100%.'],
  ['String', 'Dictionary lookup: each raw value maps to display text with an optional colour. Unmapped values show as-is.', '50501 → "Telstra AU", 50502 → "Optus AU".'],
  ['Multi-Parameter', 'Combines TWO IO elements. Lookup mode matches "primary:secondary" pairs against a dictionary; Concatenate mode joins them using a template.', 'MCC + MNC IOs combined to resolve the network operator name.'],
];

const SCENARIOS = [
  {
    title: 'Battery voltage in Volts',
    setup: 'IO Parameter: External Voltage (mV). Value Type: Value. Formula: X / 1000. Decimals: 2. Units: V.',
    result: 'A raw 12450 displays as 12.45 V in the Telemetry panel and Fleet cards.',
  },
  {
    title: 'Ignition as readable text',
    setup: 'IO Parameter: 239 Ignition. Value Type: Logic. "1" → ON (green), "0" → OFF (red).',
    result: 'Telemetry shows a coloured ON/OFF chip — and Event Rules can compare against either the label (ON) or the raw value (1).',
  },
  {
    title: 'Non-linear fuel tank',
    setup: 'IO Parameter: analog fuel sensor. Value Type: Value with a Calibration Table: 0→0, 512→25, 1024→60, 2048→100.',
    result: 'Raw readings are interpolated between the calibration points BEFORE the formula, converting an irregular sensor curve into litres or %.',
  },
  {
    title: 'Charger plugged in but not charging (Computed)',
    setup: 'Enable Computed Logic Parameter. Formula: Charger Plugged = 1 AND Charging = 0. Labels: YES / NO.',
    result: 'A virtual IO (custom1) that reads YES only while both raw conditions hold — usable in Event Rules, timers and telemetry like any physical IO.',
  },
];

const TROUBLESHOOTING = [
  ['Mapping shows no value', 'The device must actually send that IO ID — check Current Mapped Output on the device row or the raw packets. Also confirm the Telemetry Panel toggle is on.'],
  ['Formula seems ignored', 'Formulas only apply to the Value type. Remember the Calibration Table runs FIRST, then the formula receives the calibrated number as X.'],
  ['Duplicate mapping warning (amber)', 'Two mappings on the same IO ID are allowed (e.g. raw + calculated views) but Event Rules may resolve the value ambiguously — keep only one, or name them clearly.'],
  ['Computed parameter always NO', 'A source IO the device has never sent counts as 0 / false. Check each condition\'s "Now:" hint — it shows the live raw value the formula sees.'],
  ['Value vanishes when a beacon reports', 'Partial packets (e.g. BLE beacons) never clear previously received IO values — the server keeps the accumulated effective state. If a value disappears, the device sent a new (empty) reading for it.'],
  ['Changes not visible after saving', 'Mappings are per device. Use Export / Import to copy a mapping set to another device with the SAME protocol.'],
];

export const IOMappingHelpPanel = () => (
  <Sheet>
    <SheetTrigger asChild>
      <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-xs" data-testid="io-mapping-help-btn">
        <BookOpen className="w-3.5 h-3.5" /> How IO Mappings work
      </Button>
    </SheetTrigger>
    <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto z-[10000]" data-testid="io-mapping-help-panel">
      <SheetHeader>
        <SheetTitle>How IO Mappings Work</SheetTitle>
        <SheetDescription>From raw device data to readable values everywhere in the platform.</SheetDescription>
      </SheetHeader>
      <HelpDocExport slug="io-mappings" title="How IO Mappings Work" subtitle="From raw device data to readable values everywhere in the platform" targetId="help-doc-io-mappings" />
      <div id="help-doc-io-mappings" className="space-y-6 py-4">
        <Section title="1 · What an IO mapping is">
          <p>Devices send raw IO elements — numbered values like <span className="font-mono">239 = 1</span> or <span className="font-mono">66 = 12450</span>. An IO mapping tells the platform what a number means: its name, how to convert it, how to display it, and where to show it.</p>
          <p>Every mapped value flows through the same pipeline:</p>
          <Pipeline steps={['Raw IO value', 'Calibration Table (X→Y)', 'Formula (X = calibrated)', 'Value Type text / colours', 'Telemetry · Fleet · Map · Events']} />
          <p>Mappings are per device. Protocol defaults are pre-loaded, and Export / Import copies a mapping set between devices of the same protocol.</p>
        </Section>

        <Section title="2 · Setting one up">
          <p><span className="font-medium text-foreground">1.</span> Pick the <span className="font-medium text-foreground">IO Parameter</span> — search the protocol catalogue by ID or name (e.g. 239 Ignition), or use Custom ID for non-catalogue elements.</p>
          <p><span className="font-medium text-foreground">2.</span> Give it a <span className="font-medium text-foreground">Display Name</span> and an <span className="font-medium text-foreground">IO Type</span> (icon shown next to the value).</p>
          <p><span className="font-medium text-foreground">3.</span> Choose the <span className="font-medium text-foreground">Value Type</span> and fill its fields (see table below).</p>
          <p><span className="font-medium text-foreground">4.</span> Set the <span className="font-medium text-foreground">visibility toggles</span> — Telemetry Panel, Fleet Card, Map Popup, Show Icon, and Hide if Ignition OFF for values that are meaningless with the engine off.</p>
          <p><span className="font-medium text-foreground">5.</span> Click Add Mapping, then <span className="font-medium text-foreground">Save Changes</span> on the device — mappings only take effect after the device is saved.</p>
        </Section>

        <Section title="3 · Value types">
          <div className="border rounded overflow-hidden">
            <table className="w-full text-[11px]">
              <thead><tr className="bg-muted text-foreground">
                <th className="text-left p-1.5 font-medium">Type</th>
                <th className="text-left p-1.5 font-medium">Behaviour</th>
                <th className="text-left p-1.5 font-medium">Example</th>
              </tr></thead>
              <tbody className="divide-y">
                {VALUE_TYPES.map(([t, b, e]) => (
                  <tr key={t}><td className="p-1.5 font-medium text-foreground whitespace-nowrap align-top">{t}</td><td className="p-1.5 align-top">{b}</td><td className="p-1.5 align-top">{e}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-amber-700 dark:text-amber-400">The Calibration Table always runs before the Formula: raw → interpolated between your X→Y points → the result becomes X in the formula.</p>
        </Section>

        <Section title="4 · Computed Logic Parameters (virtual IOs)">
          <p>Turn on <span className="font-medium text-foreground">Computed Logic Parameter</span> to create a virtual IO that does not exist on the device. Its value is calculated from other raw IO values using an AND / OR formula and behaves like a real IO everywhere — telemetry, Fleet cards, Event Rule sources, condition timers.</p>
          <p>• It gets an automatic Virtual IO ID (<span className="font-mono">custom1</span>, <span className="font-mono">custom2</span>, …) and always outputs 1/0 with your YES/NO labels.</p>
          <p>• Conditions reference the protocol's <span className="font-medium text-foreground">raw</span> IO values — a source does not need its own mapping first.</p>
          <p>• A source the device has never reported counts as 0 / false.</p>
          <p>• The <span className="font-medium text-foreground">Now:</span> hint under each condition and the <span className="font-medium text-foreground">Current result</span> chip show the live evaluation while you build the formula.</p>
        </Section>

        <Section title="5 · Where mapped values are used">
          <p><span className="font-medium text-foreground">Live Tracking Telemetry</span> — the mapped name, converted value, unit and colour chip.</p>
          <p><span className="font-medium text-foreground">Fleet cards & Map popups</span> — controlled per mapping by the visibility toggles.</p>
          <p><span className="font-medium text-foreground">Event Rules</span> — Sensor/IO triggers and conditions compare against the raw value, the mapped numeric AND the mapped label, so <span className="font-mono">1</span>, <span className="font-mono">ON</span> or <span className="font-mono">YES</span> all match where appropriate (case-insensitive).</p>
          <p><span className="font-medium text-foreground">Current Mapped Output</span> — the device row shows the latest accumulated values so you can verify a mapping instantly.</p>
        </Section>

        <Section title="6 · Common recipes">
          {SCENARIOS.map((s, i) => (
            <div key={i} className="p-2.5 rounded border bg-muted/40 space-y-1">
              <p className="font-medium text-foreground">{s.title}</p>
              <p><span className="font-medium">Setup:</span> {s.setup}</p>
              <p><span className="font-medium">Result:</span> {s.result}</p>
            </div>
          ))}
        </Section>

        <Section title="7 · Why isn't my mapping working?">
          {TROUBLESHOOTING.map(([q, a], i) => (
            <div key={i}>
              <p className="font-medium text-foreground">{q}</p>
              <p>{a}</p>
            </div>
          ))}
        </Section>
      </div>
    </SheetContent>
  </Sheet>
);
